import { createFileRoute, Link } from '@tanstack/react-router'
import { ClockIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAssemblies } from '@/api/assemblies'
import type { Assembly } from '@/api/assemblies'
import { VoidedBadge } from '@/components/corrections/voided'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'

// Assemblies list (UI-SCREENS F-09, #134): date, Variant, qty, labor minutes, created by and the
// voided badge (a voided Assembly stays listed, greyed; 04i #135), newest first. Owner and Staff
// both see it and both record Assemblies.
export const Route = createFileRoute('/w/$slug/assemblies/')({
  component: AssembliesList,
  staticData: { breadcrumb: 'assemblies.title' },
})

function AssembliesList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const assemblies = useAssemblies(slug)

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('assemblies.title')}</h1>
        <Button asChild className="h-11">
          <Link to="/w/$slug/assemblies/new" params={{ slug }}>
            {t('assemblies.record')}
          </Link>
        </Button>
      </div>

      {assemblies.isPending ? (
        <LoadingSkeleton />
      ) : assemblies.isError || !assemblies.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : assemblies.data.length === 0 ? (
        <RecordListEmpty>{t('assemblies.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {assemblies.data.map((assembly) => (
            <AssemblyRow key={assembly.id} slug={slug} assembly={assembly} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function AssemblyRow({ slug, assembly }: { slug: string; assembly: Assembly }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      voided={Boolean(assembly.voided_at)}
      title={
        <RecordLink to="/w/$slug/assemblies/$assemblyId" params={{ slug, assemblyId: String(assembly.id) }}>
          {assembly.variant.sku} · {assembly.variant.name}
        </RecordLink>
      }
      badges={<VoidedBadge record={assembly} />}
      meta={[
        {
          icon: ClockIcon,
          label: t('assemblies.columns.laborMinutes'),
          value: t('assemblies.minutes', { count: assembly.labor_minutes }),
        },
        assembly.created_by
          ? {
              icon: UserIcon,
              label: t('assemblies.columns.createdBy'),
              value: assembly.created_by.name ?? assembly.created_by.email,
            }
          : null,
      ]}
      figure={t('assemblies.list.units', { count: assembly.qty })}
      caption={formatBusinessDate(assembly.occurred_on)}
    />
  )
}
